/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */

import React, { useCallback } from "react";
import {
	fetchScheduledReportsList,
	saveRemoteScheduledReportsList,
	clearScheduledReportsChanges,
	updateScheduledReportsChanges,
	updateScheduledReportAction,
	removeScheduledReportAction,
	addScheduledReportAction,
} from "./actions";
import { scheduledReportsInitialState, scheduledReportsReducer } from "./reducer";

const Context = React.createContext();

export const useScheduledReports = () => React.useContext(Context);

export const ScheduledReportsProvider = ({ children }) => {
	const [state, dispatch] = React.useReducer(scheduledReportsReducer, scheduledReportsInitialState);
	const { loaded } = state;

	const getScheduledReportsList = useCallback(async () => {
		if (!loaded.scheduledReport) {
			await fetchScheduledReportsList(dispatch);
		}
	}, [loaded.scheduledReport]);

	const saveScheduledReportsList = useCallback(async () => {
		const { scheduledReportsUpdates } = state;
		await saveRemoteScheduledReportsList(dispatch, Object.values(scheduledReportsUpdates));
	}, [state]);

	const clearScheduledReports = useCallback(() => {
		clearScheduledReportsChanges(dispatch);
	}, []);

	const updateScheduledReportsList = useCallback((scheduledReport) => {
		updateScheduledReportsChanges(dispatch, scheduledReport);
	}, []);

	const updateSingleScheduledReports = useCallback((scheduledReport, index) => {
		updateScheduledReportAction(dispatch, scheduledReport, index);
	}, []);

	const removeScheduledReports = useCallback((reportId, index) => {
		removeScheduledReportAction(dispatch, reportId, index);
	}, []);

	const addScheduledReports = useCallback((scheduledReport) => {
		addScheduledReportAction(dispatch, scheduledReport);
	}, []);

	return (
		<Context.Provider
			value={{
				...state,
				getScheduledReportsList,
				saveScheduledReportsList,
				saveRemoteScheduledReportsList,
				clearScheduledReports,
				updateScheduledReportsList,
				updateSingleScheduledReports,
				removeScheduledReports,
				addScheduledReports,
			}}
		>
			{children}
		</Context.Provider>
	);
};
